import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/contexts/AuthContext'
import { Button } from '@/components/ui/Button'
import { ContextMenu, ContextMenuItem } from '@/components/ui/ContextMenu'
import { SmartSearch } from '@/components/ui/SmartSearch'
import { SmartSearchResults } from '@/components/ui/SmartSearchResults'
import { SystemPreviewPanel, type SystemPreview } from '@/components/ui/SystemPreviewPanel'
import { LanguageSwitcher } from '@/components/ui/LanguageSwitcher'
import {
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  LayoutDashboard,
  Users,
  Cpu,
  Ship,
  Anchor,
  Shield,
  Key,
  Menu,
  X,
  Server,
  Package,
  FolderOpen,
  Plus,
  FileSpreadsheet,
  RefreshCw,
  Building2,
  Copy,
  Trash2,
  Filter,
  ExternalLink,
  FileCode2,
} from 'lucide-react'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { useToast } from '@/contexts/ToastContext'
import seacureLogo from '@/assets/SEACURE(CYBER SECURITY).png'
import hyundaiMipoLogo from '@/assets/Hyundai Mipo Dockyards.png'

// Some yards show a second logo beside the first; the shipyard name itself is unchanged.
const SHIPYARD_SECONDARY_LOGOS: Record<string, string> = {
  'HD Hyundai Heavy Industries': hyundaiMipoLogo,
}

// The yards that shipped with the app, in the order they've always been laid
// out. Tiles come from the shipyards TABLE now (so a yard added under
// Settings → Shipyards appears here); this list only decides where the
// familiar ones sit, and stands in as the tile set if that list can't load.
const SHIPYARD_FIXED_ORDER = [
  'HD Hyundai Heavy Industries',
  'HJ Shipbuilding and Construction',
  'Hanwha Ocean',
  'HD Hyundai Samho',
  'K Shipbuilding',
  'Korea Yanase Tongyeong Shipbuilding',
  'Samsung Heavy Industries',
] as const

const SHIPYARD_DOT_COLORS = [
  'bg-blue-500',
  'bg-emerald-500',
  'bg-amber-500',
  'bg-violet-500',
  'bg-rose-500',
  'bg-cyan-500',
] as const

function dotClassForShipyard(shipyardName: string | null | undefined): string {
  if (!shipyardName) return 'bg-slate-400'
  const i = (SHIPYARD_FIXED_ORDER as readonly string[]).indexOf(shipyardName)
  if (i >= 0) return SHIPYARD_DOT_COLORS[i % SHIPYARD_DOT_COLORS.length]
  return 'bg-slate-400'
}

function navLinkSidebarExpanded(active: boolean): string {
  return `flex w-full items-center gap-2.5 rounded px-2.5 py-2 text-[13px] transition-colors ${
    active
      ? 'bg-blue-50 font-semibold text-blue-900 shadow-[inset_3px_0_0_#245b91] dark:bg-slate-700/70 dark:text-white'
      : 'text-slate-600 hover:bg-slate-200/60 hover:text-slate-950 dark:text-slate-400 dark:hover:bg-slate-800/60 dark:hover:text-white'
  }`
}

function navLinkSidebarCollapsed(active: boolean): string {
  return `flex w-full items-center justify-center rounded-md p-2 transition-colors ${
    active
      ? 'bg-slate-200/80 font-medium text-slate-900 dark:bg-slate-700/70 dark:text-white'
      : 'text-slate-600 hover:bg-slate-200/60 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800/60 dark:hover:text-white'
  }`
}

// left accent colors per shipyard for non-selected cards
const SHIPYARD_ROW_THEME: Record<string, { borderL: string }> = {
  'Hanwha Ocean': { borderL: 'border-l-orange-500' },
  'Samsung Heavy Industries': { borderL: 'border-l-blue-500' },
  'HD Hyundai Heavy Industries': { borderL: 'border-l-green-500' },
  'HD Hyundai Samho': { borderL: 'border-l-green-500' },
  'HJ Shipbuilding and Construction': { borderL: 'border-l-violet-500' },
  'K Shipbuilding': { borderL: 'border-l-amber-500' },
  'Korea Yanase Tongyeong Shipbuilding': { borderL: 'border-l-rose-500' },
}

const UNASSIGNED_ROW_THEME = { borderL: 'border-l-slate-400' }

function shipyardRowTheme(filterKey: string): { borderL: string } {
  if (filterKey === 'none') return UNASSIGNED_ROW_THEME
  return SHIPYARD_ROW_THEME[filterKey] ?? UNASSIGNED_ROW_THEME
}

interface ShipyardGroup {
  key:           string
  shipyard_id:   number | null
  shipyard_name: string
  projects:      Project[]
  system_count:  number
}

function buildShipyardGroups(projects: Project[]): ShipyardGroup[] {
  const map = new Map<string, ShipyardGroup>()
  for (const p of projects) {
    const key = p.shipyard_id !== null ? String(p.shipyard_id) : 'none'
    if (!map.has(key)) {
      map.set(key, {
        key,
        shipyard_id:   p.shipyard_id,
        shipyard_name: p.shipyard_name ?? 'Unassigned',
        projects:      [],
        system_count:  0,
      })
    }
    const g = map.get(key)!
    g.projects.push(p)
    g.system_count += p.system_count ?? 0
  }
  return [...map.values()].sort((a, b) => {
    if (a.shipyard_id === null) return 1
    if (b.shipyard_id === null) return -1
    return a.shipyard_name.localeCompare(b.shipyard_name)
  })
}
import { canManage, canManageRoles, isSuperAdmin } from '@/lib/roles'
import { LatestReleaseCard } from '@/components/dashboard/LatestReleaseCard'
import { StatsSection } from '@/components/dashboard/StatsSection'
import { projectsApi, shipyardLogoUrl, Project, Shipyard } from '@/lib/projectsApi'
import { getShipyardImageSrc } from '@/lib/shipyards'
import { NewProjectModal } from '@/components/modals/NewProjectModal'
import { smartSearchService, type SmartSearchResult } from '@/lib/smartSearch'
import { setPendingImport } from '@/lib/pendingImport'
import {
  getDashboardPrimaryNavItems,
  dashboardNavPillClass,
  DASHBOARD_NAV_WORKSPACE_IDS,
  DASHBOARD_NAV_LIBRARY_IDS,
} from '@/config/dashboardNav'


// Cached per tab so returning to a search does not re-ask the model for
// an answer it already gave. Keyed by the exact query text; a different
// question is a different answer and gets a miss.
//
// Every access is guarded: sessionStorage throws outright in some
// contexts (private windows, embedded previews, site data blocked), and a
// search page that will not render because a cache read failed is worse
// than one that simply has no cache.
const SEARCH_CACHE_KEY = 'seacuredb:smart-search'

function readCachedSearch(query: string): SmartSearchResult | null {
  if (!query) return null
  try {
    const raw = sessionStorage.getItem(SEARCH_CACHE_KEY)
    if (!raw) return null
    const cached = JSON.parse(raw) as { query: string; result: SmartSearchResult }
    return cached.query === query ? cached.result : null
  } catch { return null }
}

function writeCachedSearch(query: string, result: SmartSearchResult) {
  try {
    sessionStorage.setItem(SEARCH_CACHE_KEY, JSON.stringify({ query, result }))
  } catch { /* over quota, or storage refused: the URL still carries the query */ }
}

export const DashboardPage: React.FC = () => {
  const { t } = useTranslation()
  const { user, signOut, isInternal, loading: authLoading } = useAuth()
  const navigate = useNavigate()
  const { success, error: showError } = useToast()

  const [projects, setProjects] = useState<Project[]>([])
  const [shipyards, setShipyards] = useState<Shipyard[]>([])
  const [contactsCount, setContactsCount] = useState(0)
  const [loading, setLoading] = useState(true)

  const [sidebarOpen, setSidebarOpen] = useState(() => {
    try {
      const v = localStorage.getItem('seacuredb-sidebar-open')
      if (v === 'false') return false
      if (v === 'true') return true
    } catch {
      /* ignore */
    }
    return true
  })
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  // Drag-and-drop state
  const [dragOver, setDragOver] = useState(false)
  const [droppedFile, setDroppedFile] = useState<File | null>(null)

  // New Project modal state
  const [showNewProjectModal, setShowNewProjectModal] = useState(false)
  const [newProjectShipyardId, setNewProjectShipyardId] = useState<number | ''>('')
  // null = all projects, 'none' = unassigned, else shipyard display name
  const [selectedShipyardFilter, setSelectedShipyardFilter] = useState<string | null>(null)
  const location = useLocation()
  const importFileInputRef = useRef<HTMLInputElement>(null)
  const [gettingStartedDismissed, setGettingStartedDismissed] = useState(false)
  // ── the result set has to outlive this component ────────────────────
  //
  // The preview panel keeps a PREVIEW from costing the results, and it
  // does. It cannot help once you actually leave — and leaving is a thing
  // the panel itself offers, via "Open full system page". Come back and
  // the dashboard has remounted with empty state: the same loop the panel
  // was built to end, reached one step later.
  //
  // So the query lives in the URL and the answer lives in sessionStorage:
  //   ?q=            makes the search a place you can return to, share or
  //                  reload, and Back restores it
  //   sessionStorage makes that restore instant and free — re-running
  //                  would spend another completion rebuilding an answer
  //                  we already had. Per tab, gone when it closes.
  const [searchParams, setSearchParams] = useSearchParams()
  const urlQuery = searchParams.get('q') ?? ''
  const [smartSearchResult, setSmartSearchResult] = useState<SmartSearchResult | null>(
    // read during the FIRST render, not in an effect: an effect paints an
    // empty result panel and then fills it, which reads as the results
    // being lost and then found
    () => readCachedSearch(urlQuery),
  )
  const [previewSystem, setPreviewSystem] = useState<SystemPreview | null>(null)

  // A ?q= with no cached answer: a link someone shared, a new tab, a
  // reload after the tab was closed. Run it once so the URL means the
  // same thing wherever it is opened. Guarded on the result already being
  // there, so the common Back case costs nothing.
  const restoredFor = useRef<string | null>(null)
  useEffect(() => {
    if (!urlQuery || smartSearchResult || restoredFor.current === urlQuery) return
    restoredFor.current = urlQuery
    void (async () => {
      const r = await smartSearchService.searchNaturalLanguage(urlQuery)
      if (r.error) return
      setSmartSearchResult(r)
      writeCachedSearch(urlQuery, r)
    })()
  }, [urlQuery, smartSearchResult])
  const [contextMenu, setContextMenu] = useState<{
    x: number
    y: number
    items: ContextMenuItem[]
  } | null>(null)
  const closeContextMenu = useCallback(() => setContextMenu(null), [])

  const shipyardGroups = useMemo(() => buildShipyardGroups(projects), [projects])

  const shipyardDisplayRows = useMemo(() => {
    const byName = new Map<string, ShipyardGroup>()
    for (const g of shipyardGroups) {
      if (g.key !== 'none') byName.set(g.shipyard_name, g)
    }
    const unassigned = shipyardGroups.find(g => g.key === 'none')
    const rows: {
      filterKey: string
      displayName: string
      logo?: string
      logo2?: string
      projectCount: number
      systemCount: number
    }[] = []

    // one tile per active shipyard row, familiar yards first and anything
    // added since after them, alphabetically. filterKey has to be the stored
    // shipyard_name — that's what the project list filters on.
    const fixedIndex = new Map(SHIPYARD_FIXED_ORDER.map((n, i) => [n as string, i]))
    const tileNames = shipyards.length > 0
      ? [...shipyards]
          .map(s => s.name)
          .sort((a, b) => {
            const ia = fixedIndex.get(a) ?? 999
            const ib = fixedIndex.get(b) ?? 999
            return ia !== ib ? ia - ib : a.localeCompare(b)
          })
      : [...SHIPYARD_FIXED_ORDER]

    const logoByName = new Map(shipyards.map(s => [s.name, shipyardLogoUrl(s)]))
    for (const name of tileNames) {
      const g = byName.get(name)
      rows.push({
        filterKey: name,
        displayName: name,
        // an uploaded logo overrides the bundled asset, which is how a yard
        // gets a new mark without a release
        logo: logoByName.get(name) ?? getShipyardImageSrc(name),
        logo2: SHIPYARD_SECONDARY_LOGOS[name],
        projectCount: g?.projects.length ?? 0,
        systemCount: g?.system_count ?? 0,
      })
    }
    if (unassigned && unassigned.projects.length > 0) {
      rows.push({
        filterKey: 'none',
        displayName: 'Unassigned',
        logo: undefined,
        logo2: undefined,
        projectCount: unassigned.projects.length,
        systemCount: unassigned.system_count,
      })
    }
    return rows
  }, [shipyardGroups, shipyards])

  const dashboardStats = useMemo(() => {
    const totalSystems = projects.reduce((s, p) => s + (p.system_count ?? 0), 0)
    const withImport = projects.filter(p => (p.import_count ?? 0) > 0)
    const hasImports = withImport.length > 0
    let lastImportLabel = ''
    if (hasImports) {
      const maxTs = Math.max(...withImport.map(p => new Date(p.updated_at).getTime()))
      lastImportLabel = new Date(maxTs).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    }
    return {
      projectCount: projects.length,
      shipyardGroupCount: shipyardGroups.length,
      totalSystems,
      totalContacts: contactsCount,
      lastImportLabel,
      hasImports,
    }
  }, [projects, shipyardGroups, contactsCount])

  const recentProjectsList = useMemo(() => {
    let list = [...projects]
    list.sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
    if (selectedShipyardFilter === 'none') {
      list = list.filter(p => p.shipyard_id === null)
    } else if (selectedShipyardFilter) {
      list = list.filter(p => (p.shipyard_name ?? '') === selectedShipyardFilter)
    }
    return list
  }, [projects, selectedShipyardFilter])

  const firstProjectWithSystems = useMemo(
    () => projects.find(p => (p.system_count ?? 0) > 0),
    [projects]
  )

  const handleSidebarNavContextMenu = useCallback(
    (e: React.MouseEvent, to: string) => {
      e.preventDefault()
      setContextMenu({
        x: e.clientX,
        y: e.clientY,
        items: [
          { label: t('dashboardPage.open'), icon: <ExternalLink className="h-4 w-4" />, onClick: () => navigate(to) },
          {
            label: t('dashboardPage.openNewTab'),
            icon: <ExternalLink className="h-4 w-4" />,
            onClick: () => window.open(to, '_blank', 'noopener,noreferrer'),
          },
        ],
      })
    },
    [navigate, t]
  )

  const openNewProjectModalWithShipyardFilter = useCallback(() => {
    if (selectedShipyardFilter === 'none') {
      setNewProjectShipyardId('')
    } else if (selectedShipyardFilter) {
      const sy = shipyards.find(s => s.name === selectedShipyardFilter)
      setNewProjectShipyardId(sy?.id ?? '')
    }
    setShowNewProjectModal(true)
  }, [selectedShipyardFilter, shipyards])

  const hasUnassignedProjects = useMemo(
    () => shipyardGroups.some(g => g.key === 'none' && g.projects.length > 0),
    [shipyardGroups]
  )

  useEffect(() => {
    if (selectedShipyardFilter === 'none' && !hasUnassignedProjects) {
      setSelectedShipyardFilter(null)
    }
  }, [selectedShipyardFilter, hasUnassignedProjects])

  const primaryNavItems = useMemo(() => getDashboardPrimaryNavItems(), [])
  const workspaceNavItems = useMemo(
    () => primaryNavItems.filter(i => (DASHBOARD_NAV_WORKSPACE_IDS as readonly string[]).includes(i.id)),
    [primaryNavItems]
  )
  const libraryNavItems = useMemo(
    () => primaryNavItems.filter(i => (DASHBOARD_NAV_LIBRARY_IDS as readonly string[]).includes(i.id)),
    [primaryNavItems]
  )

  const loadProjects = useCallback(async () => {
    setLoading(true)
    try {
      setProjects(await projectsApi.listProjects())
    } catch (err: any) {
      showError(err.message ?? t('dashboardPage.loadProjectsFailed'))
    } finally {
      setLoading(false)
    }
  }, [showError, t])

  useEffect(() => {
    if (!authLoading && user) {
      loadProjects()
      projectsApi.listShipyards().then(setShipyards).catch(() => {})
      projectsApi.listContacts().then(cs => setContactsCount(cs.length)).catch(() => {})
    }
  }, [authLoading, user, loadProjects])


  useEffect(() => {
    try {
      localStorage.setItem('seacuredb-sidebar-open', String(sidebarOpen))
    } catch {
      /* ignore */
    }
  }, [sidebarOpen])


  // scroll to workspace section when #projects hash is present
  useEffect(() => {
    if (location.hash === '#projects') {
      requestAnimationFrame(() => {
        document.getElementById('projects')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      })
    }
  }, [location.hash, location.pathname])

  const handleSignOut = async () => { await signOut() }

  const handleImportFilePick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    e.target.value = ''
    if (!f) return
    if (!f.name.match(/\.xlsx?$/i)) {
      showError(t('dashboardPage.wrongFileType'), t('dashboardPage.chooseWorkbook'))
      return
    }
    setDroppedFile(f)
    setShowNewProjectModal(true)
  }

  // Drag handlers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.dataTransfer.items?.[0]?.kind === 'file') setDragOver(true)
  }
  const handleDragLeave = (e: React.DragEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragOver(false)
  }
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragOver(false)
    const f = e.dataTransfer.files?.[0]
    if (!f) return
    if (!f.name.match(/\.xlsx?$/i)) {
      showError(t('dashboardPage.wrongFileType'), t('dashboardPage.dropWorkbook'))
      return
    }
    setDroppedFile(f)
    setShowNewProjectModal(true)
  }

  const closeNewProjectModal = () => {
    setShowNewProjectModal(false)
    setDroppedFile(null)
    setNewProjectShipyardId('')
  }

  const handleProjectCreated = (project: Project, file: File | null) => {
    closeNewProjectModal()
    if (file) {
      setPendingImport(file)
      navigate(`/projects/${project.id}/import`)
    } else {
      loadProjects()
    }
  }

  // wait for auth before rendering
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-slate-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600 dark:text-slate-400">{t('common.loading')}</p>
        </div>
      </div>
    )
  }

  return (
    <div
      className="flex min-h-screen flex-col bg-[#f4f6f8] text-slate-900 dark:bg-slate-900 dark:text-white lg:h-screen lg:overflow-hidden"
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Drag overlay */}
      {dragOver && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-blue-600/10 dark:bg-blue-400/10 border-4 border-dashed border-blue-400 dark:border-blue-500 pointer-events-none">
          <div className="text-center">
            <FileSpreadsheet className="h-16 w-16 mx-auto text-blue-500 mb-4" />
            <p className="text-2xl font-semibold text-blue-600 dark:text-blue-400">{t('dashboardPage.dropToImport')}</p>
            <p className="text-sm text-blue-500 mt-1">{t('dashboardPage.newProjectCreated')}</p>
          </div>
        </div>
      )}

      {/* New Project modal */}
      <NewProjectModal
        open={showNewProjectModal}
        droppedFile={droppedFile}
        initialShipyardId={newProjectShipyardId}
        onClose={closeNewProjectModal}
        onCreated={handleProjectCreated}
      />

      {/* header */}
      <header className="z-20 shrink-0 border-b border-slate-200 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.03)] dark:border-slate-700 dark:bg-slate-800">
        <div className="mx-auto px-4 sm:px-6 lg:px-8 max-w-none w-full">
          <div className="flex h-16 items-center justify-between">
            <div className="flex items-center gap-3 sm:gap-6">
              {/* mobile menu button - left side with logo */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white"
                aria-label={t('dashboardPage.menu')}
              >
                {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
              
              <Link to="/dashboard" className="cursor-pointer">
                <img src={seacureLogo} alt="SEACURE" className="h-9 w-auto transition-opacity hover:opacity-80 sm:h-10" />
              </Link>
              
              <nav className="hidden lg:flex items-center flex-wrap gap-1" aria-label="Main">
                {primaryNavItems.map(item => (
                    <Link
                    key={item.id}
                    to={item.to}
                    className={dashboardNavPillClass(item.isActive(location.pathname, location.hash))}
                    >
                    {item.label(t)}
                    </Link>
                ))}

              </nav>
            </div>
            
            <div className="flex items-center gap-2 lg:gap-3">
              {/* settings - desktop only */}
              <Link to="/settings" className="hidden lg:block">
                <Button variant="ghost" size="sm" className="text-sm">
                  <Settings className="h-4 w-4 mr-1.5" />
                  {t('nav.settings')}
                </Button>
              </Link>
              
              {/* email - desktop only */}
              <span className="hidden xl:inline text-sm text-slate-500 dark:text-slate-400">
                {user?.email}
              </span>
              
              {/* language switcher - always visible but compact on mobile */}
              <LanguageSwitcher />
              
              {/* sign out button - icon only on mobile */}
              <Button 
                variant="ghost" 
                size="sm"
                onClick={handleSignOut}
                className="text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
                aria-label={t('auth.signout')}
              >
                <LogOut className="h-4 w-4" />
                <span className="hidden sm:inline ml-2">{t('auth.signout')}</span>
              </Button>
            </div>
          </div>
        </div>
        
        {/* mobile navigation menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
            <nav className="px-4 py-3 space-y-1">
              {/* user info on mobile */}
              <div className="px-3 py-2 mb-2 bg-slate-100 dark:bg-slate-700 rounded-md">
                <div className="text-xs text-slate-500 dark:text-slate-400">{t('dashboardPage.loggedInAs')}</div>
                <div className="text-sm font-medium text-slate-900 dark:text-white truncate">{user?.email}</div>
              </div>
              
              {/* settings on mobile */}
              <Link 
                to="/settings" 
                className="flex items-center gap-2 px-3 py-2 text-base font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-md"
                onClick={() => setMobileMenuOpen(false)}
              >
                <Settings className="h-4 w-4" />
                {t('nav.settings')}
              </Link>
              
              <div className="border-t border-slate-200 dark:border-slate-700 my-2"></div>

                <div className="mb-2">
                <div className="px-3 py-1 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">{t('dashboardPage.workspace')}</div>
                {workspaceNavItems.map(item => {
                  const active = item.isActive(location.pathname, location.hash)
                  return (
                  <Link
                      key={item.id}
                      to={item.to}
                      className={`block px-3 py-2 text-sm font-medium rounded-md ${
                        active
                          ? 'bg-slate-200/80 dark:bg-slate-700/70 text-slate-900 dark:text-white'
                          : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'
                      }`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                      {item.label(t)}
                  </Link>
                  )
                })}
                </div>

              <div className="mb-2">
                <div className="px-3 py-1 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">{t('dashboardPage.library')}</div>
                {libraryNavItems.map(item => {
                  const active = item.isActive(location.pathname, location.hash)
                  return (
                  <Link
                      key={item.id}
                      to={item.to}
                      className={`block px-3 py-2 text-sm font-medium rounded-md ${
                        active
                          ? 'bg-slate-200/80 dark:bg-slate-700/70 text-slate-900 dark:text-white'
                          : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'
                      }`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                      {item.label(t)}
                  </Link>
                  )
                })}
              </div>
              
              {/* tools section */}
              {isInternal && (
                <div className="mt-2 border-t border-slate-200 pt-2 dark:border-slate-700">
                  <div className="px-3 py-1.5 text-[10px] font-medium uppercase tracking-wide text-slate-400">{t('nav.tools')}</div>
                  <Link
                    to="/pentest-history"
                    className="block rounded-md px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {t('dashboardPage.pentestHistory')}
                  </Link>
                  <Link
                    to="/pentest-releases"
                    className="block rounded-md px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {t('dashboardPage.pentestReleases')}
                  </Link>

                  {isSuperAdmin(user) && (
                    <>
                      <div className="my-2 border-t border-slate-200 dark:border-slate-700" />
                      <div className="px-3 py-1.5 text-[10px] font-medium uppercase tracking-wide text-slate-400">
                        {t('dashboardPage.administration')}
                </div>
                <Link
                        to="/license-management"
                        className="block rounded-md px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700"
                  onClick={() => setMobileMenuOpen(false)}
                >
                        {t('dashboardPage.licenseManagement')}
                </Link>
                      <Link
                        to="/admin/force-password-reset"
                        className="block rounded-md px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {t('dashboardPage.forcePasswordReset')}
                      </Link>
                  <Link 
                    to="/audit-logs" 
                        className="block rounded-md px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                        {t('dashboardPage.auditLogs')}
                  </Link>
                    </>
                  )}

                  {canManageRoles(user) && (
                    <>
                      <div className="my-2 border-t border-slate-200 dark:border-slate-700" />
                      <div className="px-3 py-1.5 text-[10px] font-medium uppercase tracking-wide text-slate-400">
                        {t('fields.system')}
                      </div>
                      <Link
                        to="/admin/roles"
                        className="block rounded-md px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {t('dashboardPage.roleManagement')}
                      </Link>
                    </>
                  )}
                </div>
              )}
            </nav>
          </div>
        )}
      </header>

      <div className="flex min-h-0 flex-1">
        <aside
          className={`hidden flex-none flex-col overflow-y-auto border-r border-slate-200 bg-[#f8f9fb] transition-all duration-200 ease-in-out dark:border-slate-700 dark:bg-slate-900 lg:flex lg:h-full lg:flex-col ${
            sidebarOpen ? 'w-60 px-3 py-5' : 'w-14 px-1.5 py-5'
          }`}
          aria-label="Navigation sidebar"
        >
          <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto">
            <div
              className={`text-[10px] font-medium uppercase tracking-wider text-slate-400 dark:text-slate-500 px-2 py-1.5 mb-1 ${
                sidebarOpen ? 'mt-0' : 'hidden'
              }`}
            >
              {t('dashboardPage.workspace')}
            </div>
            {/* THIS SIDEBAR IS THE ONLY NAVIGATION IN THE APP.
                A nav entry goes here, as a Link in this block — there is
                no shared nav config to edit.

                There used to be one. src/config/nav.tsx and
                components/AppHeader.tsx were added in April 2026 as
                scaffolds (the commit says so), were never imported by
                App.tsx or any page, and were carried through four
                refactors still typechecking. The System Library entry
                was added to that config, built cleanly, and vanished:
                Rollup tree-shook the whole module because nothing
                rendered it. Both files are deleted rather than left as
                the obvious-looking place that silently does nothing. */}
            <nav aria-label="Workspace" className="space-y-0.5">
              <Link
                to="/dashboard"
                onContextMenu={e => handleSidebarNavContextMenu(e, '/dashboard')}
                className={
                  sidebarOpen
                    ? navLinkSidebarExpanded(location.pathname === '/dashboard')
                    : navLinkSidebarCollapsed(location.pathname === '/dashboard')
                }
                title={!sidebarOpen ? t('nav.dashboard') : undefined}
              >
                <LayoutDashboard className="h-4 w-4 shrink-0" />
                {sidebarOpen && <span className="min-w-0 truncate">{t('nav.dashboard')}</span>}
              </Link>
              <Link
                to="/delivered-fleet"
                onContextMenu={e => handleSidebarNavContextMenu(e, '/delivered-fleet')}
                className={
                  sidebarOpen
                    ? navLinkSidebarExpanded(location.pathname === '/delivered-fleet')
                    : navLinkSidebarCollapsed(location.pathname === '/delivered-fleet')
                }
                title={!sidebarOpen ? 'Delivered Fleet' : undefined}
              >
                <Anchor className="h-4 w-4 shrink-0" />
                {sidebarOpen && <span className="min-w-0 truncate">Delivered Fleet</span>}
              </Link>
              <Link
                to="/workspace"
                onContextMenu={e => handleSidebarNavContextMenu(e, '/workspace')}
                className={
                  sidebarOpen
                    ? navLinkSidebarExpanded(location.pathname === '/workspace')
                    : navLinkSidebarCollapsed(location.pathname === '/workspace')
                }
                title={!sidebarOpen ? t('dashboardPage.systemLibrary') : undefined}
              >
                <Ship className="h-4 w-4 shrink-0" />
                {sidebarOpen && <span className="min-w-0 truncate">{t('dashboardPage.systemLibrary')}</span>}
              </Link>
              <Link
                to="/contacts"
                onContextMenu={e => handleSidebarNavContextMenu(e, '/contacts')}
                className={
                  sidebarOpen
                    ? navLinkSidebarExpanded(location.pathname === '/contacts')
                    : navLinkSidebarCollapsed(location.pathname === '/contacts')
                }
                title={!sidebarOpen ? t('nav.contacts') : undefined}
              >
                <Users className="h-4 w-4 shrink-0" />
                {sidebarOpen && <span className="min-w-0 truncate">{t('nav.contacts')}</span>}
              </Link>
              <Link
                to="/risk-assessment"
                onContextMenu={e => handleSidebarNavContextMenu(e, '/risk-assessment')}
                className={
                  sidebarOpen
                    ? navLinkSidebarExpanded(location.pathname === '/risk-assessment')
                    : navLinkSidebarCollapsed(location.pathname === '/risk-assessment')
                }
                title={!sidebarOpen ? t('dashboardPage.riskAssessment') : undefined}
              >
                <Cpu className="h-4 w-4 shrink-0" />
                {sidebarOpen && <span className="min-w-0 truncate">{t('dashboardPage.riskAssessment')}</span>}
              </Link>
              <Link
                to="/nms-configurations"
                onContextMenu={e => handleSidebarNavContextMenu(e, '/nms-configurations')}
                className={
                  sidebarOpen
                    ? navLinkSidebarExpanded(location.pathname === '/nms-configurations')
                    : navLinkSidebarCollapsed(location.pathname === '/nms-configurations')
                }
                title={!sidebarOpen ? t('nav.nmsConfigurations') : undefined}
              >
                <FileCode2 className="h-4 w-4 shrink-0" />
                {sidebarOpen && <span className="min-w-0 truncate">{t('nav.nmsConfigurations')}</span>}
              </Link>
            </nav>

            {isInternal && (
              <>
                <div
                  className={`text-[10px] font-medium uppercase tracking-wider text-slate-400 dark:text-slate-500 px-2 py-1.5 mt-4 mb-1 ${
                    sidebarOpen ? '' : 'hidden'
                  }`}
                >
                  {t('nav.tools')}
                </div>
                <nav aria-label="Tools" className="space-y-0.5">
                  <Link
                    to="/pentest-history"
                    onContextMenu={e => handleSidebarNavContextMenu(e, '/pentest-history')}
                    className={
                      sidebarOpen
                        ? navLinkSidebarExpanded(location.pathname.startsWith('/pentest-history'))
                        : navLinkSidebarCollapsed(location.pathname.startsWith('/pentest-history'))
                    }
                    title={!sidebarOpen ? t('dashboardPage.pentestHistory') : undefined}
                  >
                    <Server className="h-4 w-4 shrink-0" />
                    {sidebarOpen && <span className="min-w-0 truncate">{t('dashboardPage.pentestHistory')}</span>}
                  </Link>
                  <Link
                    to="/pentest-releases"
                    onContextMenu={e => handleSidebarNavContextMenu(e, '/pentest-releases')}
                    className={
                      sidebarOpen
                        ? navLinkSidebarExpanded(location.pathname === '/pentest-releases')
                        : navLinkSidebarCollapsed(location.pathname === '/pentest-releases')
                    }
                    title={!sidebarOpen ? t('dashboardPage.pentestReleases') : undefined}
                  >
                    <Package className="h-4 w-4 shrink-0" />
                    {sidebarOpen && <span className="min-w-0 truncate">{t('dashboardPage.pentestReleases')}</span>}
                  </Link>
                </nav>
              </>
            )}

            {isSuperAdmin(user) && (
              <>
                <div
                  className={`text-[10px] font-medium uppercase tracking-wider text-slate-400 dark:text-slate-500 px-2 py-1.5 mt-4 mb-1 ${
                    sidebarOpen ? '' : 'hidden'
                  }`}
                >
                  {t('dashboardPage.administration')}
                </div>
                <nav aria-label="Administration" className="space-y-0.5">
                  <Link
                    to="/license-management"
                    onContextMenu={e => handleSidebarNavContextMenu(e, '/license-management')}
                    className={
                      sidebarOpen
                        ? navLinkSidebarExpanded(location.pathname === '/license-management')
                        : navLinkSidebarCollapsed(location.pathname === '/license-management')
                    }
                    title={!sidebarOpen ? t('dashboardPage.licenseManagement') : undefined}
                  >
                    <Key className="h-4 w-4 shrink-0" />
                    {sidebarOpen && <span className="min-w-0 truncate">{t('dashboardPage.licenseManagement')}</span>}
                  </Link>
                  <Link
                    to="/audit-logs"
                    onContextMenu={e => handleSidebarNavContextMenu(e, '/audit-logs')}
                    className={
                      sidebarOpen
                        ? navLinkSidebarExpanded(location.pathname === '/audit-logs')
                        : navLinkSidebarCollapsed(location.pathname === '/audit-logs')
                    }
                    title={!sidebarOpen ? t('dashboardPage.auditLogs') : undefined}
                  >
                    <Shield className="h-4 w-4 shrink-0" />
                    {sidebarOpen && <span className="min-w-0 truncate">{t('dashboardPage.auditLogs')}</span>}
                  </Link>
                  <Link
                    to="/admin/force-password-reset"
                    onContextMenu={e => handleSidebarNavContextMenu(e, '/admin/force-password-reset')}
                    className={
                      sidebarOpen
                        ? navLinkSidebarExpanded(location.pathname.startsWith('/admin/force-password-reset'))
                        : navLinkSidebarCollapsed(location.pathname.startsWith('/admin/force-password-reset'))
                    }
                    title={!sidebarOpen ? t('dashboardPage.forcePasswordReset') : undefined}
                  >
                    <Key className="h-4 w-4 shrink-0" />
                    {sidebarOpen && <span className="min-w-0 truncate">{t('dashboardPage.forcePasswordReset')}</span>}
                  </Link>
                </nav>
              </>
            )}

            {canManageRoles(user) && (
              <div className="mt-4 space-y-0.5">
                <Link
                  to="/admin/roles"
                  onContextMenu={e => handleSidebarNavContextMenu(e, '/admin/roles')}
                  className={
                    sidebarOpen
                      ? navLinkSidebarExpanded(location.pathname.startsWith('/admin/roles'))
                      : navLinkSidebarCollapsed(location.pathname.startsWith('/admin/roles'))
                  }
                  title={!sidebarOpen ? t('dashboardPage.roleManagement') : undefined}
                >
                  <Shield className="h-4 w-4 shrink-0" />
                  {sidebarOpen && <span className="min-w-0 truncate">{t('dashboardPage.roleManagement')}</span>}
                </Link>
              </div>
            )}
          </div>

          <div className="mt-auto shrink-0 space-y-2 border-t border-slate-200 pt-4 dark:border-slate-700">
            <Link
              to="/settings"
              onContextMenu={e => handleSidebarNavContextMenu(e, '/settings')}
              className={
                sidebarOpen
                  ? navLinkSidebarExpanded(location.pathname === '/settings')
                  : navLinkSidebarCollapsed(location.pathname === '/settings')
              }
              title={!sidebarOpen ? t('nav.settings') : undefined}
            >
              <Settings className="h-4 w-4 shrink-0" />
              {sidebarOpen && <span className="min-w-0 truncate">{t('nav.settings')}</span>}
            </Link>
            <div className="flex justify-center">
              <button
                type="button"
                onClick={() => setSidebarOpen(o => !o)}
                className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 transition-colors hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
                aria-label={sidebarOpen ? t('dashboardPage.collapseSidebar') : t('dashboardPage.expandSidebar')}
                title={sidebarOpen ? t('dashboardPage.collapseSidebar') : t('dashboardPage.expandSidebar')}
              >
                {sidebarOpen ? <ChevronLeft className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
              </button>
            </div>
          </div>
        </aside>

        <main className="min-w-0 flex-1 bg-[#f4f6f8] lg:h-full lg:min-h-0 lg:overflow-y-auto">
          <div className="mx-auto max-w-[1640px] px-4 py-5 sm:px-6 lg:px-8">
            <input
              ref={importFileInputRef}
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              aria-hidden
              onChange={handleImportFilePick}
            />

            <section aria-label="Smart search" className="mx-auto mb-4 max-w-[1600px]">
              <SmartSearch
                placeholder={t('dashboardPage.searchPlaceholder')}
                initialQuery={urlQuery}
                onResults={r => {
                  setSmartSearchResult(r)
                  if (r.query) writeCachedSearch(r.query, r)
                }}
                // replace, not push: a search is not a step in the history
                // Sam should have to walk back through. Back from here goes
                // where he came from, not through every query he typed.
                onQueryChange={q => setSearchParams(q ? { q } : {}, { replace: true })}
                className="w-full"
              />
              {smartSearchResult && !smartSearchResult.error && (
                <div className="mt-2 max-h-[min(24rem,50vh)] overflow-y-auto">
                  <SmartSearchResults
                    result={smartSearchResult}
                    notOpenableReason={smartSearchResult.notOpenableReason ?? null}
                    onOpenLibrary={() => {
                      const projectSystemIds = smartSearchResult.results
                        .map((row: any) =>
                          smartSearchResult.tables_used?.[0] === 'project_systems'
                            ? row.id
                            : row.project_system_id)
                        .filter((id: unknown) => typeof id === 'string')
                      sessionStorage.setItem(
                        'seacuredb:workspace-smart-results',
                        JSON.stringify({ query: smartSearchResult.query, projectSystemIds }),
                      )
                      navigate(
                        `/workspace?source=smart&q=${encodeURIComponent(smartSearchResult.query)}`,
                      )
                    }}
                    /* only a system has a preview worth showing. a contact or
                       project result keeps the old navigate-to-it behaviour. */
                    onPreview={smartSearchResult.tables_used?.[0] !== 'project_systems' ? undefined : (row) => {
                      // the server enriches project_systems rows with the
                      // facts that actually disambiguate them
                      const e = (row as any)._enriched ?? {}
                      setPreviewSystem({
                        id: String((row as any).id ?? ''),
                        system_name: (row as any).system_name ?? null,
                        system_description: (row as any).system_description ?? null,
                        vendor: e.vendor ?? (row as any).vendor_name_raw ?? null,
                        function_name: e.function_name ?? (row as any).function_name ?? null,
                        cbs_grade: (row as any).cbs_grade ?? null,
                        type_approval_status: (row as any).type_approval_status ?? null,
                        hull_number: e.hull_number ?? (row as any).hull_number ?? null,
                        vessel_name: e.vessel_name ?? (row as any).vessel_name ?? null,
                        has_ra: e.has_ra, has_inventory: e.has_inventory, has_contacts: e.has_contacts,
                      })
                    }}
                    onLocate={async (table, id) => {
                      if (table === 'project_systems') {
                        navigate(`/workspace?installation=${encodeURIComponent(String(id))}`)
                        return
                      }
                      const route = await smartSearchService.locate(table, id)
                      if (route) navigate(route)
                      else showError(t('errors.notFound'), t('dashboardPage.locateFailed'))
                    }}
                  />
            </div>
              )}
            </section>

            {/* the preview lives OVER the dashboard, never instead of it */}
            <SystemPreviewPanel
              system={previewSystem}
              onClose={() => setPreviewSystem(null)}
              onOpenFull={(sys) => navigate(
                `/workspace?installation=${encodeURIComponent(sys.id)}`,
              )}
            />

            {/* Stats */}
            <section aria-label="Overview stats" className="mx-auto mb-4 max-w-[1600px]">
              <div className="flex min-h-11 flex-wrap items-center gap-x-5 gap-y-1 rounded-md border border-slate-200 bg-white px-4 py-2 shadow-[0_1px_2px_rgba(15,23,42,0.03)] dark:bg-slate-800/50">
                <button
                  type="button"
                  onClick={() =>
                    document.getElementById('projects')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                  }
                  onContextMenu={e => {
                    e.preventDefault()
                    const v = loading ? '—' : String(dashboardStats.projectCount)
                    setContextMenu({
                      x: e.clientX,
                      y: e.clientY,
                      items: [
                        {
                          label: t('dashboardPage.copyValue'),
                          icon: <Copy className="h-4 w-4" />,
                          onClick: () => {
                            void navigator.clipboard.writeText(v)
                            success(t('dashboardPage.copied'))
                          },
                        },
                        {
                          label: t('systems.viewDetails'),
                          icon: <FolderOpen className="h-4 w-4" />,
                          onClick: () => navigate('/projects'),
                        },
                      ],
                    })
                  }}
                  className="inline-flex cursor-pointer items-baseline gap-0 transition-colors hover:opacity-80"
                >
                  <span className="mr-1 text-[10px] uppercase text-slate-400">{t('dashboardPage.projects')}</span>
                  <span className="text-sm font-medium tabular-nums text-slate-900 dark:text-white">
                    {loading ? '—' : dashboardStats.projectCount}
                  </span>
                </button>
                <span className="text-slate-300 dark:text-slate-600" aria-hidden>
                  ·
                </span>
                <button
                  type="button"
                  onClick={() => navigate('/projects')}
                  onContextMenu={e => {
                    e.preventDefault()
                    const v = loading ? '—' : String(dashboardStats.totalSystems)
                    setContextMenu({
                      x: e.clientX,
                      y: e.clientY,
                      items: [
                        {
                          label: t('dashboardPage.copyValue'),
                          icon: <Copy className="h-4 w-4" />,
                          onClick: () => {
                            void navigator.clipboard.writeText(v)
                            success(t('dashboardPage.copied'))
                          },
                        },
                        {
                          label: t('systems.viewDetails'),
                          icon: <FolderOpen className="h-4 w-4" />,
                          onClick: () => navigate('/projects'),
                        },
                      ],
                    })
                  }}
                  className="inline-flex cursor-pointer items-baseline gap-0 transition-colors hover:opacity-80"
                >
                  <span className="mr-1 text-[10px] uppercase text-slate-400">{t('nav.systems')}</span>
                  <span className="text-sm font-medium tabular-nums text-slate-900 dark:text-white">
                    {loading ? '—' : dashboardStats.totalSystems}
                  </span>
                </button>
                <span className="text-slate-300 dark:text-slate-600" aria-hidden>
                  ·
                </span>
                <button
                  type="button"
                  onClick={() => navigate('/contacts')}
                  onContextMenu={e => {
                    e.preventDefault()
                    const v = loading ? '—' : String(dashboardStats.totalContacts)
                    setContextMenu({
                      x: e.clientX,
                      y: e.clientY,
                      items: [
                        {
                          label: t('dashboardPage.copyValue'),
                          icon: <Copy className="h-4 w-4" />,
                          onClick: () => {
                            void navigator.clipboard.writeText(v)
                            success(t('dashboardPage.copied'))
                          },
                        },
                        {
                          label: t('systems.viewDetails'),
                          icon: <Users className="h-4 w-4" />,
                          onClick: () => navigate('/contacts'),
                        },
                      ],
                    })
                  }}
                  className="inline-flex cursor-pointer items-baseline gap-0 transition-colors hover:opacity-80"
                >
                  <span className="mr-1 text-[10px] uppercase text-slate-400">{t('nav.contacts')}</span>
                  <span className="text-sm font-medium tabular-nums text-slate-900 dark:text-white">
                    {loading ? '—' : dashboardStats.totalContacts}
                  </span>
                </button>
                <span className="text-slate-300 dark:text-slate-600" aria-hidden>
                  ·
                </span>
                <div
                  className="inline-flex cursor-default items-baseline gap-0"
                  onContextMenu={e => {
                    e.preventDefault()
                    const v =
                      loading || !dashboardStats.hasImports ? '—' : dashboardStats.lastImportLabel
                    setContextMenu({
                      x: e.clientX,
                      y: e.clientY,
                      items: [
                        {
                          label: t('dashboardPage.copyValue'),
                          icon: <Copy className="h-4 w-4" />,
                          onClick: () => {
                            void navigator.clipboard.writeText(v)
                            success(t('dashboardPage.copied'))
                          },
                        },
                        {
                          label: t('systems.viewDetails'),
                          icon: <FolderOpen className="h-4 w-4" />,
                          onClick: () => navigate('/projects'),
                        },
                      ],
                    })
                  }}
                >
                  <span className="mr-1 text-[10px] uppercase text-slate-400">{t('dashboardPage.lastImport')}</span>
                  <span className="text-sm font-medium tabular-nums text-slate-900 dark:text-white">
                    {loading ? '—' : dashboardStats.hasImports ? dashboardStats.lastImportLabel : '—'}
                  </span>
            </div>
          </div>
            </section>

            {/* Project workspace header */}
            <section
              aria-label="Project workspace"
              className="mx-auto mb-1.5 flex max-w-[1600px] flex-row items-center justify-between gap-2"
            >
              <h2 className="text-sm font-medium tracking-tight text-slate-900 dark:text-white">{t('dashboardPage.projectWorkspace')}</h2>
              <div className="flex shrink-0 flex-wrap items-center gap-1.5">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="text-xs"
                  onClick={() => importFileInputRef.current?.click()}
                >
                  <FileSpreadsheet className="h-3.5 w-3.5" />
                  {t('dashboardPage.importWorkbook')}
                </Button>
                <Button type="button" size="sm" className="text-xs" onClick={() => setShowNewProjectModal(true)}>
                  <Plus className="h-3.5 w-3.5" />
                  {t('dashboardPage.newProject')}
              </Button>
            </div>
            </section>

            {/* Shipyards + recent projects */}
            <section
              id="projects"
              className="mx-auto mb-2 grid max-w-[1600px] grid-cols-1 gap-2 lg:grid-cols-2 lg:items-start"
            >
              <div className="rounded-lg bg-slate-50 p-2.5 dark:bg-slate-800/50">
                <p className="mb-1 text-[11px] leading-tight text-slate-600 dark:text-slate-300">
                  <span className="font-medium text-slate-900 dark:text-white">{t('dashboardPage.shipyards')}</span>
                  <span className="text-slate-400 dark:text-slate-500"> — </span>
                  {t('dashboardPage.selectShipyardHint')}
                </p>
                <div className="mt-1 grid grid-cols-3 gap-1">
                  {shipyardDisplayRows.map(row => {
                    const isSelected = selectedShipyardFilter === row.filterKey
                    const theme = shipyardRowTheme(row.filterKey)
                    const countLabel = t('dashboardPage.projectCount', { count: row.projectCount })
                  return (
                    <button
                        key={row.filterKey}
                        type="button"
                        title={`${row.filterKey === 'none' ? t('dashboardPage.unassigned') : row.displayName} — ${row.projectCount} ${countLabel}`}
                        onClick={() =>
                          setSelectedShipyardFilter(prev => (prev === row.filterKey ? null : row.filterKey))
                        }
                        onContextMenu={e => {
                          e.preventDefault()
                              const shipyardName = row.filterKey === 'none' ? t('dashboardPage.unassigned') : row.displayName
                          const shipyardId =
                            row.filterKey === 'none'
                              ? ''
                              : (shipyards.find(s => s.name === row.displayName)?.id ?? '')
                          setContextMenu({
                            x: e.clientX,
                            y: e.clientY,
                            items: [
                              {
                                label: t('dashboardPage.filterProjects'),
                                icon: <Filter className="h-4 w-4" />,
                                onClick: () => setSelectedShipyardFilter(row.filterKey),
                              },
                              {
                                label: t('dashboardPage.newProjectHere'),
                                icon: <Plus className="h-4 w-4" />,
                                onClick: () => {
                                  setNewProjectShipyardId(shipyardId === '' ? '' : shipyardId)
                                  setShowNewProjectModal(true)
                                },
                              },
                              {
                                label: t('dashboardPage.viewShipyardProjects', { name: shipyardName }),
                                icon: <FolderOpen className="h-4 w-4" />,
                                onClick: () => setSelectedShipyardFilter(row.filterKey),
                              },
                            ],
                          })
                        }}
                        className={`relative flex h-14 w-full items-center justify-center rounded-lg border border-l-[3px] p-1 transition-colors ${
                          isSelected
                            ? 'border-blue-300 border-l-blue-500 bg-blue-50 dark:border-blue-300 dark:bg-blue-900/20 dark:border-l-blue-500'
                            : `border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900/35 ${theme.borderL} hover:border-slate-300 hover:bg-slate-50/50 dark:hover:bg-slate-800/50`
                        }`}
                      >
                        {row.projectCount > 0 && (
                          <span
                            className="absolute right-1 top-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-blue-100 text-[9px] font-medium text-blue-700 dark:bg-blue-900 dark:text-blue-300"
                            title={`${row.projectCount} ${countLabel}`}
                          >
                            {row.projectCount > 99 ? '99+' : row.projectCount}
                          </span>
                        )}
                        {row.logo && row.logo2 ? (
                          <span className="flex w-full items-center justify-center gap-1">
                            <img
                              src={row.logo}
                              alt=""
                              className="max-h-7 max-w-[calc(50%-5px)] object-contain"
                            />
                            <span
                              aria-hidden="true"
                              className="h-6 w-px shrink-0 bg-slate-200 dark:bg-slate-600"
                            />
                            <img
                              src={row.logo2}
                              alt=""
                              className="max-h-7 max-w-[calc(50%-5px)] object-contain"
                            />
                          </span>
                        ) : row.logo ? (
                          <img
                            src={row.logo}
                            alt=""
                            className="max-h-8 max-w-full object-contain"
                          />
                        ) : (
                          <Building2 className="h-6 w-6 text-slate-300" />
                        )}
                    </button>
                  )
                })}
                </div>
              </div>

              <div className="rounded-lg bg-slate-50 p-2.5 dark:bg-slate-800/50">
                <div className="mb-0.5 flex items-center justify-between gap-2">
                  <p className="text-[10px] uppercase text-slate-400">
                    {t('dashboardPage.recent')}
                    {selectedShipyardFilter ? (
                      <span className="normal-case text-slate-500"> · {t('dashboardPage.shown', { count: recentProjectsList.length })}</span>
                    ) : null}
                  </p>
                  <Button variant="outline" size="sm" className="h-6 shrink-0 px-1.5 text-xs" onClick={loadProjects} title={t('common.refresh')}>
                    <RefreshCw className="h-3 w-3" />
                  </Button>
                    </div>

                <div className="max-h-[min(12rem,35vh)] space-y-0.5 overflow-y-auto pr-0.5">
                  {loading ? (
                    <p className="py-6 text-center text-sm text-slate-500 dark:text-slate-400">{t('dashboardPage.loadingProjects')}</p>
                  ) : projects.length === 0 ? (
                    <div className="py-8 text-center">
                      <FolderOpen className="mx-auto mb-2 h-8 w-8 text-slate-300 dark:text-slate-600" />
                      <p className="text-sm font-medium text-slate-600 dark:text-slate-400">{t('dashboardPage.noProjects')}</p>
                      <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">
                        {t('dashboardPage.noProjectsHint')}
                      </p>
                      <Button size="sm" className="mt-3 h-8 text-xs" onClick={() => setShowNewProjectModal(true)}>
                        <Plus className="mr-1 h-3 w-3" />
                        {t('dashboardPage.newProject')}
                      </Button>
                    </div>
                  ) : recentProjectsList.length === 0 ? (
                    selectedShipyardFilter ? (
                      <div className="py-4 text-center">
                        <p className="text-sm text-slate-600 dark:text-slate-400">
                          {t('dashboardPage.noProjectsFor')}{' '}
                          {selectedShipyardFilter === 'none'
                            ? t('dashboardPage.unassigned')
                            : shipyardDisplayRows.find(r => r.filterKey === selectedShipyardFilter)?.displayName ??
                              t('dashboardPage.thisShipyard')}
                        </p>
                        <Button size="sm" className="mt-2 h-7 text-xs" onClick={openNewProjectModalWithShipyardFilter}>
                          <Plus className="mr-1 h-3 w-3" />
                          {t('dashboardPage.createProject')}
                        </Button>
                      </div>
                    ) : (
                      <p className="py-4 text-center text-sm text-slate-500 dark:text-slate-400">
                        {t('dashboardPage.noProjectMatch')}
                      </p>
                    )
                  ) : (
                    recentProjectsList.map(p => {
                      const syName = p.shipyard_name ?? t('dashboardPage.unassigned')
                      const dotClass = dotClassForShipyard(p.shipyard_id === null ? null : p.shipyard_name ?? undefined)
                      const n = p.system_count ?? 0
                      return (
                        <div
                              key={p.id}
                          role="button"
                          tabIndex={0}
                              onClick={() => navigate(`/projects/${p.id}`)}
                          onContextMenu={e => {
                            e.preventDefault()
                            setContextMenu({
                              x: e.clientX,
                              y: e.clientY,
                              items: [
                                {
                                  label: t('dashboardPage.openProject'),
                                  icon: <FolderOpen className="h-4 w-4" />,
                                  onClick: () => navigate(`/projects/${p.id}`),
                                },
                                {
                                  label: t('dashboardPage.importWorkbook'),
                                  icon: <FileSpreadsheet className="h-4 w-4" />,
                                  onClick: () => navigate(`/projects/${p.id}/import`),
                                },
                                {
                                  label: t('dashboardPage.copyHullNumber'),
                                  icon: <Copy className="h-4 w-4" />,
                                  onClick: () => {
                                    void navigator.clipboard.writeText(p.hull_number)
                                    success(t('dashboardPage.copied'))
                                  },
                                },
                                {
                                  label: t('dashboardPage.copyImoNumber'),
                                  icon: <Copy className="h-4 w-4" />,
                                  onClick: () => {
                                    void navigator.clipboard.writeText(p.imo_number || '')
                                    success(t('dashboardPage.copied'))
                                  },
                                  disabled: !p.imo_number,
                                },
                                {
                                  divider: true,
                                  label: t('dashboardPage.deleteProject'),
                                  icon: <Trash2 className="h-4 w-4" />,
                                  onClick: async () => {
                                    if (!window.confirm(t('dashboardPage.confirmDeleteProject', { hull: p.hull_number }))) return
                                    try {
                                      await projectsApi.deleteProject(p.id)
                                      success(t('dashboardPage.projectDeleted', { hull: p.hull_number }))
                                      await loadProjects()
                                    } catch (err: any) {
                                      showError(err.message ?? t('dashboardPage.deleteProjectFailed'))
                                    }
                                  },
                                  danger: true,
                                  disabled: !canManage(user),
                                },
                              ],
                            })
                          }}
                          onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault()
                              navigate(`/projects/${p.id}`)
                            }
                          }}
                          className="flex cursor-pointer flex-row items-center justify-between gap-2 rounded-lg border border-slate-200/80 bg-white/90 px-2 py-1.5 transition-colors hover:bg-slate-50/80 dark:border-slate-700/60 dark:bg-slate-900/35 dark:hover:bg-slate-800/40"
                        >
                          <div className="min-w-0 flex flex-1 items-start gap-2">
                            <span className={`mt-0.5 h-2 w-2 shrink-0 rounded-full ${dotClass}`} aria-hidden />
                            <div className="min-w-0">
                              <div className="text-xs font-medium text-slate-900 dark:text-white">{p.hull_number}</div>
                              <div className="text-[10px] text-slate-500 dark:text-slate-400">
                                {syName}
                                {p.vessel_name ? ` · ${p.vessel_name}` : ''}
                              </div>
                              {n > 0 && (
                                <div className="text-[10px] tabular-nums text-slate-400 dark:text-slate-500">
                                  {t('dashboardPage.systemsImported', { count: n })}
                                </div>
                              )}
                                  </div>
                          </div>
                          <div className="flex shrink-0 items-center gap-1.5">
                            <span className="rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-600 bg-slate-100 dark:bg-slate-700 dark:text-slate-300">
                              {p.status || '—'}
                            </span>
                                  <button
                              type="button"
                              className="rounded-md border border-slate-200 px-2 py-1 text-[11px] text-slate-400 transition-colors hover:border-slate-300 hover:text-slate-600 dark:border-slate-600 dark:hover:border-slate-500 dark:hover:text-slate-300"
                              onClick={e => {
                                e.stopPropagation()
                                navigate(`/projects/${p.id}/import`)
                              }}
                            >
                              {t('common.import')}
                                  </button>
                            <ChevronRight className="h-4 w-4 shrink-0 text-slate-300" aria-hidden />
                    </div>
                  </div>
                )
                    })
          )}
        </div>
            </div>
            </section>

            <section id="data-completeness" aria-label={t('dashboard.growth.title')} className="mx-auto mb-2 max-w-[1600px]">
              <StatsSection />
            </section>

            <section
              aria-label="Latest release"
              className="mx-auto mb-2 grid max-w-[1600px] grid-cols-1 gap-2 lg:grid-cols-3 lg:items-start"
            >
              <LatestReleaseCard />
            </section>

            {!loading && !gettingStartedDismissed && dashboardStats.totalSystems === 0 && (
              <section
              aria-label={t('dashboardPage.gettingStarted')}
                className="mx-auto mb-1.5 mt-1 flex max-w-[1600px] flex-wrap items-start justify-between gap-2 rounded-lg bg-slate-50 px-3 py-1.5 dark:bg-slate-800/50"
              >
                <p className="min-w-0 flex-1 text-[10px] leading-snug text-slate-600 dark:text-slate-400">
                  <span className="font-medium text-slate-700 dark:text-slate-300">{t('dashboardPage.gettingStarted')}</span>{' '}
                  {shipyards.length > 0 ? '✓' : '○'} {t('dashboardPage.shipyard')} · {projects.length > 0 ? '✓' : '○'} {t('fields.project')} →{' '}
                  <button
                    type="button"
                    className="text-blue-600 underline hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                    onClick={() => importFileInputRef.current?.click()}
                  >
                    {t('dashboardPage.importWorkbook')}
                  </button>
                  {' '}
                  →{' '}
                  <button
                    type="button"
                    disabled={!firstProjectWithSystems}
                    className="text-blue-600 underline hover:text-blue-800 disabled:cursor-not-allowed disabled:opacity-40 dark:text-blue-400 dark:hover:text-blue-300"
                    onClick={() =>
                      firstProjectWithSystems && navigate(`/projects/${firstProjectWithSystems.id}`)
                    }
                  >
                    {t('dashboardPage.reviewSystems')}
                  </button>
                  {' · '}
                  <button
                    type="button"
                    className="text-blue-600 underline hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                    onClick={() => setShowNewProjectModal(true)}
                  >
                    {t('dashboardPage.createProject')}
                  </button>
                </p>
                <button
                  type="button"
                  onClick={() => setGettingStartedDismissed(true)}
                  className="shrink-0 rounded p-0.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-300"
                  aria-label={t('dashboardPage.dismissGettingStarted')}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </section>
            )}
      </div>
        </main>
      </div>
      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          items={contextMenu.items}
          onClose={closeContextMenu}
        />
      )}
    </div>
  )
}
